import { useMemo } from 'react';

import { usePage } from '../../../../contexts/usePageContext';
import { CommonState } from '../../../../redux/_common/commonReducers';
import { useAppSelector } from '../../../../redux/hooks';
import { getBaseDomain } from '../../../../redux/pageDomain';
import { RootState } from '../../../../redux/store';
import { findStructure, getTranslation } from '../../../../utils/dataTypes';
import { displayValueByType } from '@/utils/structureUtils';
import { InfoArraySection, InfoArrayRow } from '../types';

const useInfoArrays = () => {
 const { pageName } = usePage();
 const selector = useAppSelector(
  (state: RootState) => state[getBaseDomain(pageName)],
 ) as CommonState;
 const { item, structure, loading } = selector;

 const tabNames = useMemo(() => {
  const tabs = new Set<string>();
  structure?.forEach((field) => {
   if (field.Group) {
    const groupPattern = /^(.+?)_(.+)$/;
    const match = field.Group.match(groupPattern);
    if (match) {
     const [, tabName] = match;
     tabs.add(tabName);
    }
   }
  });
  return Array.from(tabs);
 }, [structure]);

 const arrayStructureFields = useMemo(() => {
  if (!structure) {
   return [];
  }
  return structure
   .filter((field) => field.Type === 'array' && field.Priority)
   .sort((left, right) => (left.Priority ?? 0) - (right.Priority ?? 0));
 }, [structure]);

 const sections = useMemo(() => {
  return arrayStructureFields.map((arrayField) => {
   const rows = (item?.[arrayField.Name] as Record<string, unknown>[] | undefined)
    ?.map((arrayItem) => {
     const mappedRows = Object.entries(arrayItem)
      .map(([key, value]) => {
       const innerStructureField = findStructure(arrayField.InnerStructure, key);
       if (!innerStructureField?.Priority) {
        return null;
       }
       const mappedRow: InfoArrayRow = {
        key,
        translation: getTranslation(structure, `${arrayField.Name}.${key}`, true),
        value: displayValueByType(value, innerStructureField),
       };
       return {
        priority: innerStructureField.Priority ?? 0,
        row: mappedRow,
       };
      })
      .filter((row): row is { priority: number; row: InfoArrayRow } => Boolean(row))
      .sort((left, right) => left.priority - right.priority)
      .map((entry) => entry.row);

     return mappedRows;
    }) ?? [];

   const section: InfoArraySection = {
    ...arrayField,
    Data: rows,
   };

   return section;
  });
 }, [arrayStructureFields, item, structure]);

 return {
  item,
  loading,
  sections,
  tabNames,
 };
};

export default useInfoArrays;
